import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Navigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';

/**
 * "Awaiting access" screen for a signed-in user who holds no active
 * membership (e.g. a fresh self-registration). Standalone — rendered
 * outside the app shell so a roleless user never sees the nav. They
 * sit here until a superadmin grants them a role (Access requests),
 * after which "Check again" picks up the new membership.
 */
export function AccessPending() {
  const { user, loading, refresh, logout } = useAuth();

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ pt: 8 }}>
        <Stack alignItems="center">
          <CircularProgress />
        </Stack>
      </Container>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  // Granted while waiting → straight into the app.
  if (user.memberships.some((m) => m.is_active)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Container maxWidth="sm" sx={{ pt: 10 }}>
      <Stack spacing={3} alignItems="flex-start">
        <Typography variant="h4">Access pending</Typography>
        <Typography color="text.secondary">
          Your account is verified, but it doesn't have access to any
          workspace yet. An administrator will grant you access — you'll
          be able to sign in normally once they do.
        </Typography>
        <Typography color="text.secondary">
          Signed in as <strong>{user.email}</strong>.
        </Typography>
        <Box>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={() => refresh()}>
              Check again
            </Button>
            <Button variant="text" onClick={() => logout()}>
              Sign out
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
}
